import { IRegEdLeadTimeOverridePrompt, setRegEdLeadTimeOverrideListener } from '@Queries/RegEdCompliance';
import * as Api from '@ViewModels';
import * as React from 'react';
import { useEventLogging } from '../../../../models/Logging';
import { toast } from 'sonner';
import { RegEdLeadTimeOverrideDialog } from '../RegEdLeadTimeOverrideDialog';

/**
 * Mounted once at the app root. A compliance submission is filed after its parent is already
 * scheduled, so the flow that started it is usually gone by the time a rejection lands — there is no
 * component left to own this dialog.
 */
export const RegEdLeadTimeOverrideHost: React.FC = () => {
	const { logApiError } = useEventLogging('RegEdLeadTimeOverrideHost');
	const [prompt, setPrompt] = React.useState<IRegEdLeadTimeOverridePrompt | null>(null);
	const [isSubmitting, setIsSubmitting] = React.useState(false);

	React.useEffect(() => {
		setRegEdLeadTimeOverrideListener(next => {
			setPrompt(next);
			setIsSubmitting(false);
		});
		return () => setRegEdLeadTimeOverrideListener(null);
	}, []);

	const handleSubmit = async (reason: string) => {
		if (!prompt) {
			return;
		}
		setIsSubmitting(true);
		let filed = false;
		try {
			filed = await prompt.retry(reason);
		} catch (error) {
			// The retry carries the caller's post-filing work, which can throw. Swallowing it silently
			// would drop the dialog to idle with no explanation and nothing recorded.
			logApiError('RegEdLeadTimeOverrideRetry-Error', Api.asApiError(error));
			toast.error('We could not submit that for review. Please try again.');
		} finally {
			// Also on the throwing path: leaving this set disables Cancel too, stranding the advisor in a
			// dialog with no way out.
			setIsSubmitting(false);
		}
		if (filed) {
			// A rejected retry re-fires the listener with the new message, so only clear on success.
			setPrompt(null);
			toast.success('Submitted for compliance review.');
		}
	};

	return (
		<RegEdLeadTimeOverrideDialog
			rejection={prompt}
			isSubmitting={isSubmitting}
			onCancel={() => {
				prompt?.onDismissed?.();
				setPrompt(null);
			}}
			onSubmit={handleSubmit}
		/>
	);
};
